import React, { useCallback, useState } from "react";
import { ChevronDownIcon, SearchIcon, TypingIcon } from "@omega/icons";
import {
    Icon,
    LinearProgress,
    ListBox,
    PickerSurface as OmegaPickerSurface,
    Spinner,
    StatePlaceholder,
    type CollectionState,
} from "@omega/ui";
import { getOmpSearchString } from "@src/api/main/OMPTypes";
import {
    wsDepotCardsServices,
    type WsDepotCard,
    type WsDepotCardFilter,
} from "@src/businessModules/WorkShop/api/wsDepotCards";
import { BusinessStockObjValue, formatStockObjText } from "@src/components/business-data-display";
import { formatUnresolvedIdForDisplay } from "@src/components/business-data-entry/formatUnresolvedIdForDisplay";
import { useQueryData } from "@src/hooks/useQueryData";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { PickerOptionCard } from "@shared/ui/picker-option";
import { PickerSurface } from "@shared/ui/picker-surface";
import type { BusinessPickerFieldRenderWindowContentProps, BusinessPickerFieldWrapperProps } from "../../types";
import { BusinessPickerField } from "../BusinessPickerField";
import "./WsDepotCardPickerField.scss";

/** Локальные критерии поиска карточки УП в окне picker. */
interface WsDepotCardPickerSearchValues {
    /** Номер карточки. */
    num: string;
    /** Обозначение ТМЦ. */
    stockobjSign: string;
    /** Наименование ТМЦ. */
    stockobjName: string;
    /** Заводской код ТМЦ. */
    stockobjNomNum: string;
}

const EMPTY_SEARCH: WsDepotCardPickerSearchValues = {
    num: "",
    stockobjSign: "",
    stockobjName: "",
    stockobjNomNum: "",
};

/** Пропсы {@link WsDepotCardPickerField}. */
export interface WsDepotCardPickerFieldProps
    extends Omit<BusinessPickerFieldWrapperProps<number>, "value" | "onChange" | "windowTitle" | "summaryFormat"> {
    /** Выбранный код карточки; `null` — значение не выбрано. */
    value: number | null;
    /**
     * Колбэк изменения выбранной карточки.
     * @param cardCode код карточки или `null` после сброса
     */
    onChange?: (cardCode: number | null) => void;
    /**
     * Базовый фильтр API (например `DivisionCode` отправителя). \
     * Критерии поиска из окна мержатся поверх.
     */
    flt?: WsDepotCardFilter;
    /**
     * Заголовок overlay-окна.
     * @default "Выбор карточки"
     */
    windowTitle?: string;
}

/**
 * Собирает API-фильтр из заполненных полей поиска.
 * @param search локальные строки поиска
 * @param baseFlt внешний базовый фильтр
 */
function buildSearchFilter(search: WsDepotCardPickerSearchValues, baseFlt?: WsDepotCardFilter): WsDepotCardFilter {
    return {
        ...baseFlt,
        Num: getOmpSearchString(search.num.trim(), { fullTextSearch: true }),
        StockobjSign: getOmpSearchString(search.stockobjSign.trim(), { fullTextSearch: true }),
        StockobjName: getOmpSearchString(search.stockobjName.trim(), { fullTextSearch: true }),
        StockobjNomNum: getOmpSearchString(search.stockobjNomNum.trim(), { fullTextSearch: true }),
    };
}

/**
 * Есть ли хотя бы один непустой критерий поиска.
 * @param search локальные строки поиска
 */
function hasActiveSearch(search: WsDepotCardPickerSearchValues): boolean {
    return (
        search.num.trim() !== "" ||
        search.stockobjSign.trim() !== "" ||
        search.stockobjName.trim() !== "" ||
        search.stockobjNomNum.trim() !== ""
    );
}

/**
 * Краткая сводка заполненных критериев для свёрнутой полоски.
 * @param search критерии последнего сабмита
 */
function formatAppliedFiltersSummary(search: WsDepotCardPickerSearchValues): string {
    const parts: string[] = [];
    if (search.num.trim() !== "") {
        parts.push(`№${search.num.trim()}`);
    }
    if (search.stockobjSign.trim() !== "") {
        parts.push(search.stockobjSign.trim());
    }
    if (search.stockobjName.trim() !== "") {
        parts.push(search.stockobjName.trim());
    }
    if (search.stockobjNomNum.trim() !== "") {
        parts.push(search.stockobjNomNum.trim());
    }
    return parts.join(" · ");
}

/**
 * Форматирует краткую подпись выбранной карточки в trigger.
 * @param card карточка УП
 */
function formatCardSummary(card: WsDepotCard): string {
    return `№${card.Num}`;
}

/**
 * Text-value опции для a11y / typeahead.
 * @param card карточка УП
 */
function formatCardTextValue(card: WsDepotCard): string {
    const stockObjText = formatStockObjText(card.Stockobj);
    return stockObjText === "" ? `Карточка ${card.Num}` : `Карточка ${card.Num} — ${stockObjText}`;
}

/**
 * Состояние коллекции ListBox по результату поиска.
 * @param options `null` — поиск ещё не запускался; иначе загруженные карточки
 * @param isLoading blocking-загрузка
 */
function resolveCollectionState(options: WsDepotCard[] | null, isLoading: boolean): CollectionState {
    if (isLoading) {
        return "loading";
    }
    if (options == null) {
        return "idle";
    }
    if (options.length === 0) {
        return "empty";
    }
    return "idle";
}

/** Контент fullscreen-окна выбора карточки УП. */
function WsDepotCardPickerWindowContent({
    value,
    submit,
    close,
    readonly,
    flt,
}: BusinessPickerFieldRenderWindowContentProps<number> & {
    flt?: WsDepotCardFilter;
}): React.ReactElement {
    const [draftSearch, setDraftSearch] = useState<WsDepotCardPickerSearchValues>(EMPTY_SEARCH);
    const [appliedSearch, setAppliedSearch] = useState<WsDepotCardPickerSearchValues | null>(null);
    const [filtersExpanded, setFiltersExpanded] = useState(true);

    const searchEnabled = !readonly && appliedSearch != null && hasActiveSearch(appliedSearch);

    const searchQuery = useQueryData({
        queryKey: ["picker", "wsDepotCard", flt, appliedSearch],
        queryFn: async ({ signal }) => {
            if (appliedSearch == null) {
                return [];
            }
            return wsDepotCardsServices.load(
                {
                    flt: buildSearchFilter(appliedSearch, flt),
                    include: ["Stockobj", "Measure", "ProductionType"],
                },
                { signal },
            );
        },
        enabled: searchEnabled,
    });

    const options = searchEnabled ? (searchQuery.data ?? []) : null;
    const isLoading = searchQuery.isLoading === true;
    const isRefreshing = searchQuery.isFetching === true && searchQuery.isLoading === false;
    const collectionState = resolveCollectionState(options, isLoading);
    const selectedKey = value[0] ?? null;
    const canSubmitSearch = !readonly && hasActiveSearch(draftSearch);
    const canClearFilters = !readonly && (hasActiveSearch(draftSearch) || appliedSearch != null);
    const appliedSummary = appliedSearch != null ? formatAppliedFiltersSummary(appliedSearch) : "";

    const patchDraftSearch = (patch: Partial<WsDepotCardPickerSearchValues>) => {
        setDraftSearch((prev) => ({ ...prev, ...patch }));
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSubmitSearch) {
            return;
        }
        setAppliedSearch({ ...draftSearch });
        setFiltersExpanded(false);
    };

    const handleClearFilters = () => {
        setDraftSearch(EMPTY_SEARCH);
        setAppliedSearch(null);
        setFiltersExpanded(true);
    };

    return (
        <PickerSurface className="ws_depot_card_picker">
            <PickerSurface.Controls className="ws_depot_card_picker__filters">
                {filtersExpanded ? (
                    <form className="ws_depot_card_picker__filters_form" onSubmit={handleSearchSubmit}>
                        <Input
                            label="Номер карточки"
                            value={draftSearch.num}
                            onChange={(event) => {
                                patchDraftSearch({ num: event.target.value });
                            }}
                            disabled={readonly}
                        />
                        <Input
                            label="Обозначение ТМЦ"
                            value={draftSearch.stockobjSign}
                            onChange={(event) => {
                                patchDraftSearch({ stockobjSign: event.target.value });
                            }}
                            disabled={readonly}
                        />
                        <Input
                            label="Наименование ТМЦ"
                            value={draftSearch.stockobjName}
                            onChange={(event) => {
                                patchDraftSearch({ stockobjName: event.target.value });
                            }}
                            disabled={readonly}
                        />
                        <Input
                            label="Заводской код ТМЦ"
                            value={draftSearch.stockobjNomNum}
                            onChange={(event) => {
                                patchDraftSearch({ stockobjNomNum: event.target.value });
                            }}
                            disabled={readonly}
                        />
                        <div className="ws_depot_card_picker__filters_actions">
                            <div className="ws_depot_card_picker__filters_submit_row">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={!canClearFilters}
                                    onClick={handleClearFilters}
                                >
                                    Сбросить
                                </Button>
                                <Button type="submit" fullWidth disabled={!canSubmitSearch} loading={isLoading}>
                                    Найти
                                </Button>
                            </div>
                            {appliedSearch != null ? (
                                <Button
                                    type="button"
                                    variant="ghost"
                                    fullWidth
                                    disabled={readonly}
                                    onClick={() => {
                                        setFiltersExpanded(false);
                                    }}
                                >
                                    Свернуть
                                </Button>
                            ) : null}
                        </div>
                    </form>
                ) : (
                    <button
                        type="button"
                        className="ws_depot_card_picker__filters_summary"
                        disabled={readonly}
                        aria-expanded={false}
                        onClick={() => {
                            setFiltersExpanded(true);
                        }}
                    >
                        <span className="ws_depot_card_picker__filters_summary_text">
                            <span className="ws_depot_card_picker__filters_summary_title">Критерии поиска</span>
                            {appliedSummary !== "" ? (
                                <span className="ws_depot_card_picker__filters_summary_values">{appliedSummary}</span>
                            ) : null}
                        </span>
                        <Icon icon={ChevronDownIcon} size={20} className="ws_depot_card_picker__filters_summary_icon" />
                    </button>
                )}
            </PickerSurface.Controls>
            <PickerSurface.Body className="ws_depot_card_picker__body">
                <OmegaPickerSurface mode="fullscreen" className="ws_depot_card_picker__surface" aria-label="Карточки УП">
                    {isRefreshing ? (
                        <LinearProgress aria-label="Обновление списка" className="ws_depot_card_picker__refresh" />
                    ) : null}
                    {collectionState === "idle" && options == null ? (
                        <div className="spp-ui-list-box" data-slot="list-box" data-state="idle" role="status">
                            <div data-slot="state">
                                <StatePlaceholder
                                    size="md"
                                    title="Заполните критерии и нажмите «Найти»"
                                    icon={<Icon icon={TypingIcon} size="100%" />}
                                />
                            </div>
                        </div>
                    ) : (
                        <ListBox
                            aria-label="Карточки УП"
                            items={options ?? []}
                            state={collectionState}
                            selectionMode="single"
                            selectedKey={selectedKey}
                            isReadOnly={readonly}
                            getKey={(item) => item.Code}
                            getTextValue={formatCardTextValue}
                            emptyContent={
                                <StatePlaceholder
                                    size="md"
                                    title="Карточки не найдены"
                                    icon={<Icon icon={SearchIcon} size="100%" />}
                                />
                            }
                            loadingContent={<StatePlaceholder size="md" title="Загрузка..." icon={<Spinner size="md" />} />}
                            onSelectionChange={(key) => {
                                if (key == null) {
                                    return;
                                }
                                const code = typeof key === "number" ? key : Number(key);
                                if (!Number.isFinite(code)) {
                                    return;
                                }
                                submit([code]);
                            }}
                            renderItem={(item) => (
                                <PickerOptionCard
                                    title={`Карточка ${item.Num}`}
                                    // detailsLayout="tiles"
                                    details={[
                                        {
                                            label: "ТМЦ",
                                            value: <BusinessStockObjValue stockObj={item.Stockobj} mode="wrap" />,
                                        },
                                        { label: "Тип продукции", value: item.ProductionType?.Name },
                                    ]}
                                />
                            )}
                        />
                    )}
                </OmegaPickerSurface>
            </PickerSurface.Body>
            <PickerSurface.Footer>
                <PickerSurface.CloseButton onClick={close}>Вернуться назад</PickerSurface.CloseButton>
            </PickerSurface.Footer>
        </PickerSurface>
    );
}

/**
 * Picker-поле выбора цеховой карточки УП.
 *
 * Trigger — summary по коду; окно — четыре критерия + кнопка «Найти» \
 * (запрос только по сабмиту, после него фильтры сворачиваются). \
 * Список — `@omega/ui` ListBox в `PickerSurface mode="fullscreen"`.
 *
 * @example
 * ```tsx
 * <WsDepotCardPickerField
 *   value={leaveCardCode}
 *   onChange={setLeaveCardCode}
 *   flt={{ DivisionCode: senderDivisionCode }}
 * />
 * ```
 */
export function WsDepotCardPickerField({
    value,
    onChange,
    flt,
    label = "Карточка",
    required = false,
    error,
    disabled = false,
    placeholder,
    windowTitle = "Выбор карточки",
}: WsDepotCardPickerFieldProps): React.ReactElement {
    const selectedIds = value == null ? [] : [value];

    const resolveQuery = useQueryData({
        queryKey: ["picker", "wsDepotCard", "resolve", value, flt],
        queryFn: async ({ signal }) => {
            if (value == null || value <= 0) {
                return null;
            }
            return wsDepotCardsServices.loadOne(
                {
                    flt: { ...flt, Codes: [value] },
                    include: ["Stockobj", "Measure", "ProductionType"],
                },
                { signal },
            );
        },
        enabled: value != null && value > 0,
    });

    const summaryFormat = useCallback(
        (count: number): string => {
            if (count === 0 || value == null) {
                return "";
            }
            const card = resolveQuery.data;
            if (card != null) {
                return formatCardSummary(card);
            }
            return `Код: ${formatUnresolvedIdForDisplay(value)}`;
        },
        [resolveQuery.data, value],
    );

    const handleChange = useCallback(
        (ids: number[]) => {
            onChange?.(ids[0] ?? null);
        },
        [onChange],
    );

    return (
        <BusinessPickerField<number>
            label={label}
            required={required}
            error={error}
            disabled={disabled}
            placeholder={placeholder}
            windowTitle={windowTitle}
            windowKey="workshop.wsDepotCard.picker"
            value={selectedIds}
            onChange={handleChange}
            summaryFormat={summaryFormat}
            showConfirmFooter={false}
            renderWindowContent={(props) => <WsDepotCardPickerWindowContent {...props} flt={flt} />}
        />
    );
}
